"use client";
/**
 * SANATAN DECODE — style proof v5. 21s @ 30fps, 1280x720.
 *
 * v4 was denser but still "not forcing me to watch". Yash's own diagnosis was the key one:
 * the rishi was shown centre, faded out, then reintroduced smaller — same object, animated
 * twice, with a gap. It should have TRAVELLED and SHRUNK.
 *
 * Research (Vox / Johnny Harris breakdowns) named four things v4 got wrong:
 *   1. ON TWOS. Vox animates at 12fps inside a 24fps timeline. Perfectly smooth motion is
 *      what makes explainers read synthetic. Every animated value here is quantised to
 *      every 2nd frame (useStep) — the audio stays smooth, the motion steps.
 *   2. TRANSFORMATION, NOT CUT-AND-REINTRODUCE. Objects that appear in more than one beat
 *      are ONE instance living at stage level, travelling and rescaling between poses.
 *      The rishi is one object from 3.9s to 10.2s. The marigold thread is one object from
 *      6.6s to the end — it collapses to a dot, rides through the arch beat, and reopens
 *      as the inheritance V. That is the graphic match.
 *   3. ONE ACCENT. KUMKUM is the only "look here" colour and it is spent once per beat.
 *      Gold and marigold are demoted to structure.
 *   4. ANCHOR FIRST. Frame 0 opens ON the manuscript, already large. Evidence, then question.
 * Plus halftone + grain so the frame reads printed rather than rendered.
 */
import React from "react";
import {
  AbsoluteFill, Audio, Img, staticFile,
  useCurrentFrame, useVideoConfig, interpolate, spring, Easing, random,
} from "remotion";
import { loadFont as loadDeva } from "@remotion/google-fonts/NotoSansDevanagari";
import { loadFont as loadInter } from "@remotion/google-fonts/Inter";

const { fontFamily: deva } = loadDeva("normal", { weights: ["400", "600", "700", "800", "900"], subsets: ["devanagari", "latin"] });
const { fontFamily: inter } = loadInter("normal", { weights: ["500", "700", "800"], subsets: ["latin"] });

const INK = "#22304F";
const GOLD = "#BE9433";
const MARIGOLD = "#D98324";
const KUMKUM = "#C0392B";        // THE accent. Used once per beat, never decoratively.
const MUTED_C = "#8E8771";

const FPS = 30;
const f = (s: number) => s * FPS;

/** ON TWOS — quantise every animated value to every 2nd frame. */
const useStep = () => Math.floor(useCurrentFrame() / 2) * 2;

const W: Record<number, [number, number, string]> = {
  0: [0.0, 0.15, "आप"], 1: [0.21, 0.35, "जिस"], 2: [0.41, 0.71, "गोत्र"], 3: [0.74, 0.82, "का"],
  4: [0.87, 1.06, "नाम"], 5: [1.13, 1.36, "लेते"], 6: [1.42, 1.64, "हैं"],
  8: [2.03, 2.16, "वो"], 9: [2.26, 2.47, "आपका"], 10: [2.58, 3.01, "सरनेम"], 11: [3.05, 3.24, "नहीं"],
  12: [3.32, 3.6, "है।"],
  15: [4.35, 4.7, "ऋषि"],
  26: [8.2, 8.42, "एक"], 27: [8.45, 8.52, "ही"], 28: [8.63, 9.17, "रक्त-रेखा"],
  34: [11.61, 12.03, "शास्त्र"],
  38: [13.41, 14.03, "सगोत्र"], 39: [14.41, 14.76, "विवाह"], 40: [14.86, 15.27, "नहीं।"],
  45: [17.43, 17.86, "विज्ञान"],
};

/** Keyframe a value across absolute seconds, stepped on twos. */
const useKeys = (keys: [number, number][]) => {
  const frame = useStep();
  return interpolate(frame, keys.map((k) => f(k[0])), keys.map((k) => k[1]), {
    extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.inOut(Easing.cubic),
  });
};

const useSpringAt = (at: number, damping = 9, stiffness = 110, mass = 1.1) => {
  const frame = useStep();
  const { fps } = useVideoConfig();
  return spring({ frame: frame - f(at), fps, config: { damping, stiffness, mass } });
};

/* ══ TEXTURE — printed, not rendered ═══════════════════════════ */
const Halftone: React.FC = () => (
  <AbsoluteFill style={{ pointerEvents: "none", opacity: 0.1, mixBlendMode: "multiply" }}>
    <svg width="100%" height="100%">
      <defs>
        <pattern id="ht" width="6" height="6" patternUnits="userSpaceOnUse">
          <circle cx="3" cy="3" r="1.15" fill={INK} />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#ht)" />
    </svg>
  </AbsoluteFill>
);

const Grain: React.FC = () => {
  const frame = useStep();
  return (
    <AbsoluteFill style={{ pointerEvents: "none", opacity: 0.13, mixBlendMode: "multiply" }}>
      <svg width="100%" height="100%">
        <filter id="gr5">
          <feTurbulence type="fractalNoise" baseFrequency="0.92" numOctaves="3" seed={frame % 5} />
        </filter>
        <rect width="100%" height="100%" filter="url(#gr5)" />
      </svg>
    </AbsoluteFill>
  );
};

/* ══ BACKGROUND ════════════════════════════════════════════════ */
const BG_STOPS = [
  { at: 0, color: "#F2E6CC", dir: "l" },
  { at: 3.92, color: "#F7E5BB", dir: "r" },
  { at: 5.82, color: "#F1DFCF", dir: "l" },
  { at: 10.58, color: "#F8E6BF", dir: "u" },
  { at: 15.76, color: "#ECE4D4", dir: "r" },
] as const;

const BgShift: React.FC = () => {
  const frame = useStep();
  return (
    <AbsoluteFill style={{ backgroundColor: BG_STOPS[0].color }}>
      {BG_STOPS.map((s, i) => {
        const p = interpolate(frame, [f(s.at), f(s.at) + 14], [0, 1], {
          extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.inOut(Easing.cubic),
        });
        const inset = s.dir === "l" ? `0 ${(1 - p) * 100}% 0 0` : s.dir === "r" ? `0 0 0 ${(1 - p) * 100}%` : `${(1 - p) * 100}% 0 0 0`;
        return <AbsoluteFill key={i} style={{ backgroundColor: s.color, clipPath: `inset(${inset})` }} />;
      })}
    </AbsoluteFill>
  );
};

const Rings: React.FC<{ s: number; c: string }> = ({ s, c }) => (
  <svg width={s} height={s} viewBox="0 0 100 100">
    {[46, 34, 22, 10].map((r, i) => <circle key={i} cx={50} cy={50} r={r} fill="none" stroke={c} strokeWidth={2.4} />)}
  </svg>
);
const DotGrid: React.FC<{ s: number; c: string }> = ({ s, c }) => (
  <svg width={s} height={s} viewBox="0 0 100 100">
    {Array.from({ length: 7 }, (_, r) => Array.from({ length: 7 }, (_, q) => <circle key={`${r}-${q}`} cx={8 + q * 14} cy={8 + r * 14} r={2.6} fill={c} />))}
  </svg>
);
const PetalFan: React.FC<{ s: number; c: string }> = ({ s, c }) => (
  <svg width={s} height={s} viewBox="-50 -50 100 100">
    {Array.from({ length: 9 }, (_, i) => (
      <path key={i} d="M0 0 C 9 -16, 9 -32, 0 -44 C -9 -32, -9 -16, 0 0" fill="none" stroke={c} strokeWidth={2.2} transform={`rotate(${-80 + i * 20})`} />
    ))}
  </svg>
);
const ArchOutline: React.FC<{ s: number; c: string }> = ({ s, c }) => (
  <svg width={s} height={s * 0.82} viewBox="0 0 100 82">
    <path d="M6 82 L6 44 Q10 24 26 18 Q38 12 44 2 Q50 9 56 2 Q62 12 74 18 Q90 24 94 44 L94 82" fill="none" stroke={c} strokeWidth={2.6} />
  </svg>
);
const Toran: React.FC<{ s: number; c: string }> = ({ s, c }) => (
  <svg width={s} height={s * 0.4} viewBox="0 0 200 80">
    <path d="M0 6 Q50 40 100 6 Q150 40 200 6" fill="none" stroke={c} strokeWidth={2.4} />
    {Array.from({ length: 11 }, (_, i) => (
      <g key={i}>
        <line x1={10 + i * 18} y1={16 + Math.abs(Math.sin(i)) * 8} x2={10 + i * 18} y2={38 + (i % 3) * 12} stroke={c} strokeWidth={1.8} />
        <circle cx={10 + i * 18} cy={42 + (i % 3) * 12} r={4} fill={c} />
      </g>
    ))}
  </svg>
);
const Band: React.FC<{ s: number; c: string }> = ({ s, c }) => (
  <svg width={s} height={s * 0.22} viewBox="0 0 200 44">
    <rect x={0} y={8} width={200} height={9} rx={4} fill={c} />
    <rect x={0} y={26} width={140} height={5} rx={2.5} fill={c} opacity={0.7} />
  </svg>
);
const Kalash: React.FC<{ s: number; c: string }> = ({ s, c }) => (
  <svg width={s} height={s} viewBox="0 0 100 100">
    <path d="M32 40 Q22 62 32 84 L68 84 Q78 62 68 40 Z" fill="none" stroke={c} strokeWidth={2.6} />
    <rect x={28} y={32} width={44} height={9} rx={4} fill="none" stroke={c} strokeWidth={2.4} />
    <path d="M50 32 L50 20 M42 22 Q50 6 58 22" fill="none" stroke={c} strokeWidth={2.4} />
  </svg>
);
const SHAPES = { Rings, DotGrid, PetalFan, ArchOutline, Toran, Band, Kalash } as const;

type BgEl = { k: keyof typeof SHAPES; at: number; out: number; x: number; y: number; s: number; op?: number; from: "l" | "r" | "t" | "b"; spin?: number };
const BG_ELS: BgEl[] = [
  { k: "Rings", at: 0.0, out: 3.5, x: 96, y: 430, s: 210, from: "l", spin: 8 },
  { k: "DotGrid", at: 0.5, out: 3.3, x: 1010, y: 118, s: 150, from: "t" },
  { k: "Toran", at: 0.9, out: 3.6, x: 300, y: 46, s: 420, from: "t" },
  { k: "Band", at: 1.7, out: 3.4, x: 820, y: 606, s: 330, from: "r" },
  { k: "PetalFan", at: 2.4, out: 3.7, x: 60, y: 96, s: 190, from: "l", spin: -10 },
  { k: "PetalFan", at: 3.9, out: 5.5, x: 940, y: 380, s: 250, from: "r", spin: 12 },
  { k: "Rings", at: 4.1, out: 5.4, x: 130, y: 200, s: 190, from: "l", spin: -8 },
  { k: "Kalash", at: 4.5, out: 5.6, x: 1060, y: 96, s: 130, from: "t" },
  { k: "DotGrid", at: 4.8, out: 5.5, x: 150, y: 520, s: 140, from: "b" },
  { k: "ArchOutline", at: 5.8, out: 10.2, x: 470, y: 120, s: 340, from: "t" },
  { k: "DotGrid", at: 6.2, out: 10.1, x: 66, y: 108, s: 150, from: "l" },
  { k: "Band", at: 6.9, out: 10.2, x: 120, y: 618, s: 330, from: "l" },
  { k: "Rings", at: 7.6, out: 10.3, x: 1120, y: 560, s: 180, from: "r", spin: 10 },
  { k: "Toran", at: 8.3, out: 10.2, x: 700, y: 52, s: 430, from: "t" },
  { k: "PetalFan", at: 9.1, out: 10.3, x: 1130, y: 140, s: 170, from: "r", spin: -12 },
  { k: "Toran", at: 10.6, out: 15.4, x: 60, y: 48, s: 460, from: "t" },
  { k: "Toran", at: 10.9, out: 15.4, x: 760, y: 48, s: 460, from: "t" },
  { k: "Rings", at: 11.4, out: 15.3, x: 96, y: 250, s: 200, from: "l", spin: 9 },
  { k: "Rings", at: 11.7, out: 15.3, x: 1120, y: 250, s: 200, from: "r", spin: -9 },
  { k: "Kalash", at: 12.6, out: 15.4, x: 96, y: 476, s: 140, from: "b" },
  { k: "Kalash", at: 12.9, out: 15.4, x: 1104, y: 476, s: 140, from: "b" },
  { k: "DotGrid", at: 13.9, out: 15.4, x: 596, y: 588, s: 130, from: "b" },
  { k: "DotGrid", at: 15.8, out: 21.4, x: 90, y: 120, s: 160, from: "l" },
  { k: "Rings", at: 16.1, out: 21.4, x: 700, y: 540, s: 190, from: "b", spin: 8 },
  { k: "Band", at: 16.9, out: 21.4, x: 96, y: 624, s: 340, from: "l" },
  { k: "PetalFan", at: 17.6, out: 21.4, x: 1160, y: 600, s: 180, from: "r", spin: 14 },
  { k: "ArchOutline", at: 18.4, out: 21.4, x: 96, y: 236, s: 240, from: "l" },
];

const BgElement: React.FC<{ e: BgEl }> = ({ e }) => {
  const frame = useStep();
  const { fps } = useVideoConfig();
  const sIn = spring({ frame: frame - f(e.at), fps, config: { damping: 13, stiffness: 60, mass: 1.3 } });
  const sOut = spring({ frame: frame - f(e.out), fps, config: { damping: 200, stiffness: 55, mass: 0.7 } });
  if (frame < f(e.at) - 2 || frame > f(e.out) + 34) return null;
  const D = 300;
  const vx = e.from === "l" ? -1 : e.from === "r" ? 1 : 0;
  const vy = e.from === "t" ? -1 : e.from === "b" ? 1 : 0;
  const drift = interpolate(frame - f(e.at), [0, 260], [0, 26], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const dx = vx * D * (1 - sIn) + vx * D * 1.4 * sOut + drift * vx * 0.4;
  const dy = vy * D * (1 - sIn) + vy * D * 1.4 * sOut + Math.sin((frame + e.x) / 46) * 7;
  const spin = (e.spin ?? 0) * (1 - sIn) + (e.spin ?? 0) * sOut * 1.5;
  const Shape = SHAPES[e.k];
  return (
    <div style={{
      position: "absolute", left: e.x, top: e.y,
      opacity: (e.op ?? 0.26) * Math.min(1, sIn * 1.4) * (1 - Math.min(1, sOut * 1.3)),
      transform: `translate(${dx}px, ${dy}px) rotate(${spin}deg) scale(${0.82 + sIn * 0.18})`,
    }}>
      <Shape s={e.s} c={GOLD} />
    </div>
  );
};

const Mandala: React.FC = () => {
  const frame = useStep();
  const rot = interpolate(frame, [0, 640], [0, 52]);
  const breathe = 1 + Math.sin(frame / 55) * 0.035;
  const petal = (i: number, r: number, len: number, w: number) => (
    <path key={`${r}-${i}`} d={`M0 ${-r} C ${w} ${-r - len * 0.55}, ${w} ${-r - len}, 0 ${-r - len} C ${-w} ${-r - len}, ${-w} ${-r - len * 0.55}, 0 ${-r}`}
      fill="none" stroke={GOLD} strokeWidth={2} transform={`rotate(${i * (360 / 16)})`} />
  );
  return (
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
      <svg width={1180} height={1180} viewBox="-590 -590 1180 1180" style={{ opacity: 0.18 }}>
        <g transform={`rotate(${rot}) scale(${breathe})`}>
          {Array.from({ length: 16 }, (_, i) => petal(i, 150, 120, 42))}
          {Array.from({ length: 16 }, (_, i) => petal(i, 300, 150, 54))}
          <g transform={`rotate(${360 / 32})`}>{Array.from({ length: 16 }, (_, i) => petal(i, 460, 100, 40))}</g>
          <circle r={132} fill="none" stroke={GOLD} strokeWidth={2} />
          <circle r={584} fill="none" stroke={GOLD} strokeWidth={2} />
        </g>
      </svg>
    </AbsoluteFill>
  );
};

const Frieze: React.FC<{ y: number; flip?: boolean }> = ({ y, flip }) => {
  const frame = useStep();
  const unit = 44;
  const shift = (frame * (flip ? -0.25 : 0.25)) % unit;
  return (
    <svg width={1400} height={26} style={{ position: "absolute", left: -unit + shift, top: y, transform: flip ? "scaleY(-1)" : undefined, opacity: 0.45 }}>
      <line x1={0} y1={22} x2={1400} y2={22} stroke={GOLD} strokeWidth={1.5} />
      {Array.from({ length: 34 }, (_, i) => (
        <g key={i} transform={`translate(${i * unit},0)`}>
          <path d={`M6 22 Q6 8 ${unit / 2} 8 Q${unit - 6} 8 ${unit - 6} 22`} fill="none" stroke={GOLD} strokeWidth={1.5} />
          <circle cx={unit / 2} cy={4} r={2.6} fill={GOLD} />
        </g>
      ))}
    </svg>
  );
};

const Petals: React.FC = () => {
  const frame = useStep();
  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      {Array.from({ length: 18 }, (_, i) => {
        const seed = `v5${i}`;
        const x = random(seed) * 1280;
        const sp = 0.4 + random(seed + "s") * 0.7;
        const y = ((random(seed + "y") * 800 + frame * sp) % 830) - 60;
        const sway = Math.sin((frame + i * 40) / 34) * 30;
        const r = 3 + random(seed + "r") * 4.5;
        return <div key={i} style={{
          position: "absolute", left: x + sway, top: y, width: r * 2, height: r * 1.25,
          borderRadius: "50%", background: MARIGOLD, opacity: 0.26,
          transform: `rotate(${frame * 1.4 + i * 30}deg)`,
        }} />;
      })}
    </AbsoluteFill>
  );
};

/* ══ WORDS ═════════════════════════════════════════════════════ */
const Word: React.FC<{
  idx: number; size: number; color?: string; weight?: number;
  strike?: boolean; underline?: boolean; outAt?: number;
}> = ({ idx, size, color = INK, weight = 800, strike, underline, outAt }) => {
  const [start] = W[idx];
  const frame = useStep();
  const { fps } = useVideoConfig();
  const sIn = spring({ frame: frame - f(start), fps, config: { damping: 9, stiffness: 150, mass: 0.8 } });
  const sOut = outAt ? spring({ frame: frame - f(outAt), fps, config: { damping: 200, stiffness: 80, mass: 0.5 } }) : 0;
  const grow = interpolate(frame, [f(start) + 2, f(start) + 12], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.out(Easing.cubic) });
  return (
    <span style={{
      position: "relative", display: "inline-block",
      fontFamily: deva, fontWeight: weight, fontSize: size, lineHeight: 1.38, color,
      opacity: Math.min(1, sIn * 1.5) * (1 - Math.min(1, sOut * 1.4)),
      transform: `translateY(${(1 - sIn) * 34 + sOut * 90}px) rotate(${9 * (1 - sIn) - 14 * sOut}deg) scale(${0.86 + sIn * 0.14})`,
      transformOrigin: "50% 140%",
    }}>
      {W[idx][2]}
      {strike && <span style={{ position: "absolute", left: -4, top: "53%", height: Math.max(4, size * 0.075), width: `calc((100% + 8px) * ${grow})`, background: KUMKUM, borderRadius: 4 }} />}
      {underline && <span style={{ position: "absolute", left: 0, bottom: -6, height: 4, width: `calc(100% * ${grow})`, background: GOLD, borderRadius: 3, opacity: 0.8 }} />}
    </span>
  );
};

const Row: React.FC<{ children: React.ReactNode; gap?: number }> = ({ children, gap = 16 }) => (
  <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: `0 ${gap}px` }}>{children}</div>
);

/* ══ PERSISTENT OBJECTS — the v5 fix ═══════════════════════════
   These live at stage level, NOT inside a beat. They are never re-created.        */

/** ONE rishi: swings in centre-large at 3.9, TRAVELS + SHRINKS to the left at 5.6-6.3,
    holds as the lineage anchor, swings out at 10.2. Never fades and re-enters. */
const PersistentRishi: React.FC = () => {
  const frame = useStep();
  const sIn = useSpringAt(3.9, 8, 90);
  const sOut = useSpringAt(10.2, 200, 70, 0.5);
  const left = useKeys([[3.9, 471], [5.6, 471], [6.3, 158], [10.2, 158]]);
  const top = useKeys([[3.9, 148], [5.6, 148], [6.3, 214], [10.2, 214]]);
  const h = useKeys([[3.9, 372], [5.6, 372], [6.3, 318], [10.2, 318]]);
  if (frame < f(3.7) || frame > f(10.9)) return null;
  const pend = Math.sin((frame - f(3.9)) / 34) * 1.3 * sIn;
  return (
    <div style={{
      position: "absolute", left, top,
      transform: `translate(${-260 * (1 - sIn)}px, ${-300 * (1 - sIn) + 200 * sOut}px) rotate(${13 * (1 - sIn) + pend - 26 * sOut}deg)`,
      transformOrigin: "50% -28%",
      opacity: Math.min(1, sIn * 1.6) * (1 - Math.min(1, sOut * 1.4)),
    }}>
      <Img src={staticFile("sanatan/rishi.png")} style={{ height: h, filter: "drop-shadow(0 18px 22px rgba(80,50,20,0.28))" }} />
    </div>
  );
};

/**
 * ONE marigold thread. Draws as the bloodline (6.6-7.7), collapses to a single dot at the
 * centre (10.0-10.4), rides through the shastra beat as that dot, then REOPENS at 16.6 as
 * the inheritance V. Same colour, same stroke weight — a graphic match, not a new object.
 */
const PersistentThread: React.FC = () => {
  const frame = useStep();
  const Y = 396, X0 = 372, X1 = 928;
  const CX = 448, PY = 232, CY = 388;

  const draw = useKeys([[6.6, 0], [7.7, 1]]);
  const collapse = useKeys([[10.0, 0], [10.5, 1]]);      // → a dot at centre
  const reopen = useKeys([[16.6, 0], [17.15, 1]]);       // → the V
  const sway = Math.sin(frame / 21) * 9 * draw * (1 - collapse);

  if (frame < f(6.5)) return null;

  // dot position: centre of the lineage, then parked between the carriers
  const dotX = interpolate(collapse, [0, 1], [(X0 + X1) / 2, 640]);
  const dotY = interpolate(collapse, [0, 1], [Y - 26, 300]);
  const parkX = interpolate(reopen, [0, 1], [dotX, CX]);
  const parkY = interpolate(reopen, [0, 1], [dotY, CY]);

  const lineEnd = X0 + (X1 - X0) * draw;
  const vis = 1 - collapse;

  return (
    <svg width={1280} height={720} style={{ position: "absolute", pointerEvents: "none" }}>
      {/* phase 1 — the bloodline */}
      <g opacity={vis}>
        <path d={`M${X0} ${Y} Q ${(X0 + X1) / 2} ${Y - 26 - sway} ${lineEnd} ${Y - (26 + sway) * Math.sin(Math.PI * draw)}`}
          fill="none" stroke={MARIGOLD} strokeWidth={4} strokeLinecap="round" />
        {[0.25, 0.5, 0.75].map((t, i) => draw > t ? (
          <circle key={i} cx={X0 + (X1 - X0) * t} cy={Y - (26 + sway) * Math.sin(Math.PI * t)} r={7} fill="#FBF3E2" stroke={GOLD} strokeWidth={3} />
        ) : null)}
      </g>
      {/* phase 2 — the surviving dot */}
      <circle cx={parkX} cy={parkY} r={7 * collapse * (1 - reopen) + 4 * reopen} fill={MARIGOLD} opacity={collapse} />
      {/* phase 3 — the same thread, reopened as inheritance */}
      <g opacity={reopen}>
        <line x1={CX} y1={CY} x2={CX - 110 * reopen} y2={CY - (CY - PY - 38) * reopen} stroke={MARIGOLD} strokeWidth={4} strokeLinecap="round" />
        <line x1={CX} y1={CY} x2={CX + 110 * reopen} y2={CY - (CY - PY - 38) * reopen} stroke={MARIGOLD} strokeWidth={4} strokeLinecap="round" />
      </g>
    </svg>
  );
};

/* ══ BEAT-LOCAL PROPS ══════════════════════════════════════════ */
type SwingCfg = { inAt: number; outAt: number; from?: "left" | "right" | "top"; amp?: number; travel?: number };
const useSwing = ({ inAt, outAt, from = "left", amp = 17, travel = 240 }: SwingCfg) => {
  const frame = useStep();
  const sIn = useSpringAt(inAt, 8, 95, 1.15);
  const sOut = useSpringAt(outAt, 200, 75, 0.5);
  const sign = from === "right" ? -1 : 1;
  const pend = Math.sin((frame - f(inAt)) / 34) * 1.4 * sIn * (1 - sOut);
  const dx = from === "top" ? 0 : travel * sign * (1 - sIn) - travel * 1.5 * sign * sOut;
  const dy = from === "top" ? -travel * (1 - sIn) + travel * 2 * sOut : 70 * (1 - sIn) + 190 * sOut;
  return {
    transform: `translate(${dx}px, ${dy}px) rotate(${amp * sign * (1 - sIn) + pend - 30 * sign * sOut}deg) scale(${0.9 + 0.1 * sIn - 0.12 * sOut})`,
    transformOrigin: "50% -28%",
    opacity: Math.min(1, sIn * 1.6) * (1 - Math.min(1, sOut * 1.4)),
  } as React.CSSProperties;
};

const Prop: React.FC<{ src: string; h?: number; w?: number; left: number; top: number } & SwingCfg> = ({ src, h, w, left, top, ...cfg }) => {
  const style = useSwing(cfg);
  const frame = useStep();
  if (frame < f(cfg.inAt) - 4 || frame > f(cfg.outAt) + 30) return null;
  return (
    <div style={{ position: "absolute", left, top, ...style }}>
      <Img src={staticFile(src)} style={{ ...(h ? { height: h } : {}), ...(w ? { width: w } : {}), filter: "drop-shadow(0 18px 22px rgba(80,50,20,0.28))" }} />
    </div>
  );
};

const Disc: React.FC<{ at: number; out: number; x: number; y: number; r: number; color: string; op?: number }> = ({ at, out, x, y, r, color, op = 0.14 }) => {
  const frame = useStep();
  const s = useSpringAt(at, 14, 55, 1.2);
  const o = useSpringAt(out, 200, 60, 0.7);
  const drift = interpolate(frame - f(at), [0, 200], [0, 46], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  if (s <= 0.001 || frame > f(out) + 30) return null;
  return <div style={{
    position: "absolute", left: x - r, top: y - r, width: r * 2, height: r * 2, borderRadius: "50%",
    background: color, opacity: op * s * (1 - o),
    transform: `translateX(${(1 - s) * -260 + drift + o * 320}px) scale(${0.7 + s * 0.3})`,
  }} />;
};

const Beat: React.FC<{ from: number; to: number; children: React.ReactNode }> = ({ from, to, children }) => {
  const frame = useStep();
  if (frame < f(from - 0.55) || frame >= f(to + 0.7)) return null;
  return <AbsoluteFill>{children}</AbsoluteFill>;
};

/* ══ CAMERA ════════════════════════════════════════════════════ */
const CAM: [number, number, number, number][] = [
  [0, 1.09, 0, 26],      // ANCHOR FIRST — open pushed in on the manuscript
  [1.4, 1.0, 0, 0],
  [3.92, 1.06, 22, -12],
  [5.82, 1.0, -18, 8],
  [10.58, 1.05, 12, -10],
  [15.76, 1.0, -14, 6],
  [21.1, 1.08, 8, -8],
];
const useCamera = () => {
  const frame = useStep();
  const t = CAM.map((c) => f(c[0]));
  const e = { extrapolateLeft: "clamp" as const, extrapolateRight: "clamp" as const, easing: Easing.inOut(Easing.quad) };
  const s = interpolate(frame, t, CAM.map((c) => c[1]), e);
  const x = interpolate(frame, t, CAM.map((c) => c[2]), e);
  const y = interpolate(frame, t, CAM.map((c) => c[3]), e);
  return `translate(${x}px, ${y}px) scale(${s})`;
};

/* ══ BEATS ═════════════════════════════════════════════════════ */
const BeatA: React.FC = () => (
  <>
    <Disc at={0.0} out={3.6} x={880} y={300} r={250} color={MARIGOLD} />
    {/* ANCHOR: the manuscript is already on screen at frame 0, large. Evidence, then question. */}
    <Prop src="sanatan/manuscript.png" w={700} left={310} top={368} inAt={-0.4} outAt={3.55} from="right" amp={12} travel={120} />
    <div style={{ position: "absolute", left: 0, right: 0, top: 132 }}>
      <Row gap={14}>{[0, 1, 2, 3, 4, 5, 6].map((i) => <Word key={i} idx={i} size={38} color={MUTED_C} weight={600} underline={i === 2} outAt={3.6} />)}</Row>
      <div style={{ height: 16 }} />
      <Row>
        <Word idx={8} size={74} weight={900} outAt={3.62} />
        <Word idx={9} size={74} weight={900} outAt={3.64} />
        <Word idx={10} size={74} weight={900} color={KUMKUM} strike outAt={3.66} />
        <Word idx={11} size={74} weight={900} outAt={3.68} />
        <Word idx={12} size={74} weight={900} outAt={3.7} />
      </Row>
    </div>
  </>
);

const BeatB: React.FC = () => {
  const halo = useSpringAt(4.35, 11, 80);
  const gone = useSpringAt(5.5, 200, 70);
  return (
    <>
      <Disc at={3.9} out={5.5} x={640} y={300} r={280} color={GOLD} op={0.18} />
      <svg width={1280} height={720} style={{ position: "absolute", opacity: halo * 0.55 * (1 - gone) }}>
        <circle cx={612} cy={250} r={128 + halo * 34} fill="none" stroke={GOLD} strokeWidth={2} />
        <circle cx={612} cy={250} r={96 + halo * 22} fill="none" stroke={MARIGOLD} strokeWidth={1.5} />
      </svg>
      <div style={{ position: "absolute", left: 0, right: 0, top: 544, textAlign: "center" }}>
        <Row><Word idx={15} size={72} weight={900} color={KUMKUM} underline outAt={5.52} /></Row>
      </div>
    </>
  );
};

const BeatC: React.FC = () => (
  <>
    <Disc at={5.8} out={10.25} x={300} y={330} r={230} color={KUMKUM} op={0.1} />
    <Disc at={6.6} out={10.25} x={1000} y={330} r={230} color={INK} op={0.09} />
    <Prop src="sanatan/modern_man.png" h={336} left={952} top={200} inAt={7.0} outAt={10.25} from="right" amp={16} />
    <div style={{ position: "absolute", left: 372, width: 556, top: 452, textAlign: "center" }}>
      <Row gap={14}>
        <Word idx={26} size={50} weight={800} outAt={10.2} />
        <Word idx={27} size={50} weight={800} outAt={10.22} />
        <Word idx={28} size={62} weight={900} color={KUMKUM} underline outAt={10.24} />
      </Row>
    </div>
  </>
);

const BeatDE: React.FC = () => {
  const tag = useSpringAt(11.61, 10, 150);
  const tagOut = useSpringAt(15.4, 200, 80);
  const archStyle = useSwing({ inAt: 11.0, outAt: 15.45, from: "left", amp: 7, travel: 150 });
  const AW = 900, AH = 344;
  const archPath = `M0 ${AH} L0 ${AH * 0.52}
    Q ${AW * 0.06} ${AH * 0.30} ${AW * 0.20} ${AH * 0.22}
    Q ${AW * 0.34} ${AH * 0.13} ${AW * 0.42} 0
    Q ${AW * 0.5} ${AH * 0.10} ${AW * 0.58} 0
    Q ${AW * 0.66} ${AH * 0.13} ${AW * 0.80} ${AH * 0.22}
    Q ${AW * 0.94} ${AH * 0.30} ${AW} ${AH * 0.52} L ${AW} ${AH} Z`;
  return (
    <>
      <Disc at={10.6} out={15.4} x={640} y={280} r={300} color={MARIGOLD} op={0.13} />
      <Prop src="sanatan/couple.png" w={392} left={444} top={62} inAt={10.65} outAt={15.42} from="top" amp={11} travel={280} />
      <div style={{ position: "absolute", left: 190, top: 336, ...archStyle }}>
        <svg width={AW} height={AH} style={{ overflow: "visible" }}>
          <path d={archPath} fill="#FFFCF2" stroke={GOLD} strokeWidth={2.5} />
        </svg>
        <div style={{ position: "absolute", left: 0, top: 116, width: AW, textAlign: "center" }}>
          <div style={{
            display: "inline-block", fontFamily: deva, fontSize: 22, fontWeight: 800, letterSpacing: 6,
            color: "#F7EFDD", background: INK, padding: "6px 22px", borderRadius: 3,
            opacity: Math.min(1, tag * 1.5) * (1 - tagOut),
            transform: `translateY(${(1 - tag) * 18}px) rotate(${6 * (1 - tag)}deg)`,
          }}>{W[34][2]}</div>
          <div style={{ height: 18 }} />
          <Row gap={18}>
            <Word idx={38} size={70} weight={900} outAt={15.4} />
            <Word idx={39} size={70} weight={900} outAt={15.42} />
            <Word idx={40} size={70} weight={900} color={KUMKUM} outAt={15.44} />
          </Row>
        </div>
      </div>
    </>
  );
};

const BeatF: React.FC = () => {
  const frame = useStep();
  const CX = 448, PY = 232, CY = 388;
  const pA = useSpringAt(15.9, 9, 140);
  const pB = useSpringAt(16.2, 9, 140);
  const kid = useSpringAt(17.15, 8, 150);
  const term = useSpringAt(17.43, 10, 120);
  const bob = Math.sin(frame / 24) * 5;

  const Allele: React.FC<{ x: number; fill: string; opacity?: number }> = ({ x, fill, opacity = 1 }) => (
    <rect x={x - 7} y={-20} width={14} height={40} rx={7} fill={fill} opacity={opacity} />
  );
  const Carrier: React.FC<{ x: number; s: number; ph: number }> = ({ x, s, ph }) => (
    <g opacity={s} transform={`translate(${x},${PY + Math.sin((frame + ph) / 26) * 6}) scale(${0.8 + s * 0.2}) rotate(${8 * (1 - s)})`}>
      <circle r={44} fill="#FDF8EC" stroke={INK} strokeWidth={4} />
      <Allele x={-15} fill={INK} opacity={0.22} />
      <Allele x={15} fill={KUMKUM} />
    </g>
  );

  return (
    <>
      <Disc at={15.8} out={22} x={1010} y={330} r={270} color={INK} op={0.09} />
      <Prop src="sanatan/dna.png" h={520} left={940} top={110} inAt={15.85} outAt={22} from="right" amp={14} />
      <svg width={1280} height={720} style={{ position: "absolute" }}>
        <Carrier x={CX - 128} s={pA} ph={0} />
        <Carrier x={CX + 128} s={pB} ph={40} />
        <g opacity={kid} transform={`translate(${CX},${CY + bob}) scale(${0.78 + kid * 0.22}) rotate(${-10 * (1 - kid)})`}>
          <circle r={46} fill={KUMKUM} opacity={0.14} />
          <circle r={46} fill="none" stroke={KUMKUM} strokeWidth={5} />
          <Allele x={-15} fill={KUMKUM} />
          <Allele x={15} fill={KUMKUM} />
        </g>
      </svg>
      <div style={{
        position: "absolute", left: 84, top: 486, width: 730, textAlign: "center",
        opacity: Math.min(1, term * 1.4),
        transform: `translateY(${(1 - term) * 34}px) rotate(${4 * (1 - term)}deg)`,
      }}>
        <div style={{ fontFamily: inter, fontSize: 42, fontWeight: 800, letterSpacing: 6, color: KUMKUM }}>CONSANGUINITY</div>
        <div style={{ height: 8 }} />
        <div style={{ fontFamily: deva, fontSize: 25, fontWeight: 700, color: INK, opacity: 0.8 }}>विज्ञान ने इसे एक नाम दिया।</div>
      </div>
    </>
  );
};

const Furniture: React.FC = () => {
  const frame = useStep();
  const { durationInFrames } = useVideoConfig();
  const p = interpolate(frame, [0, durationInFrames], [0, 1]);
  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      <Frieze y={8} />
      <Frieze y={686} flip />
      <div style={{ position: "absolute", top: 44, left: 52, display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 4, height: 18, background: MARIGOLD, borderRadius: 2 }} />
        <span style={{ fontFamily: deva, fontWeight: 700, fontSize: 17, letterSpacing: 3, color: INK, opacity: 0.6 }}>सनातन डिकोड</span>
      </div>
      <div style={{ position: "absolute", left: 0, bottom: 0, height: 3, width: `${p * 100}%`, background: MARIGOLD }} />
    </AbsoluteFill>
  );
};

export const SanatanDecodeGotraV5: React.FC = () => {
  const cam = useCamera();
  return (
    <AbsoluteFill style={{ backgroundColor: "#F2E6CC", overflow: "hidden" }}>
      <Audio src={staticFile("sanatan/gotra.mp3")} />
      <BgShift />
      <Img src={staticFile("sanatan/bg_paper.png")} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.5, mixBlendMode: "multiply" }} />
      <Mandala />
      <BgKitV5 />

      <AbsoluteFill style={{ transform: cam }}>
        <Beat from={0} to={3.75}><BeatA /></Beat>
        <Beat from={3.9} to={5.6}><BeatB /></Beat>
        <Beat from={5.8} to={10.4}><BeatC /></Beat>
        <Beat from={10.6} to={15.6}><BeatDE /></Beat>
        <Beat from={15.8} to={21.1}><BeatF /></Beat>

        {/* stage-level: never re-created, only transformed */}
        <PersistentRishi />
        <PersistentThread />
      </AbsoluteFill>

      <Petals />
      <Halftone />
      <Grain />
      <Furniture />
    </AbsoluteFill>
  );
};

const BgKitV5: React.FC = () => (
  <AbsoluteFill style={{ pointerEvents: "none" }}>
    {BG_ELS.map((e, i) => <BgElement key={i} e={e} />)}
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
